import { createApp, watch } from 'vue';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginVue from '@bugsnag/plugin-vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faLink, faUser, faPowerOff } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/css/index.css';
import VueSweetalert2 from 'vue-sweetalert2';
import VueObserveVisibility from 'vue-observe-visibility';
import CountryFlag from 'vue-country-flag-next';
import * as filestack from 'filestack-js';
import {
        configure, defineRule, Field,
} from 'vee-validate';
import { localize } from '@vee-validate/i18n';
import * as rules from '@vee-validate/rules';
import en from '@vee-validate/i18n/dist/locale/en.json';
import nl from '@vee-validate/i18n/dist/locale/nl.json';
import fr from '@vee-validate/i18n/dist/locale/fr.json';
import es from '@vee-validate/i18n/dist/locale/es.json';
import toast from 'utils/toast';
import auth0 from './auth/authWrapper';
import { consumeReturnTo } from './auth/returnTo';
import store from './store/index';
// Your app initialization logic goes here
import App from './App.vue';
import router from './router';
import { createModuleGuard } from './router/moduleGuard';
// Custom components
import BaseLayoutModifier from './components/BaseLayoutModifier.vue';
import BaseBlock from './components/BaseBlock.vue';
import BaseBackground from './components/BaseBackground.vue';
import BasePageHeading from './components/BasePageHeading.vue';
import BaseNavigation from './components/BaseNavigation.vue';
// Custom directives
import clickRipple from './directives/clickRipple';
import toggleClass from './directives/toggleClass';
// If you don't need the styles, do not connect
import 'sweetalert2/dist/sweetalert2.min.css';
import './assets/scss/tailwind.css';

Bugsnag.start({
        apiKey: '58f627a9cb9aa4dc8642806705b3c189',
        plugins: [new BugsnagPluginVue()],
        onUncaughtException: (event) => {
                // report error to bugsnag
                Bugsnag.notify(event);
        },
        // report errors to bugnag dashboard
        autoTrackSessions: true,
        enabledReleaseStages: ['production', 'staging', 'development'],
});
const bugsnagVue = Bugsnag.getPlugin('vue');

const app = createApp(App);
// Register the module-access guard before installing the router, so it applies
// to vue-router's initial navigation (which fires synchronously on install).
router.beforeEach(createModuleGuard(() => (auth0 as any).user?.value));
// Install the router BEFORE Auth0 so `$router` is available to the Auth0 SDK
// while it handles the redirect callback — the SDK uses it to navigate to the
// originally requested route (appState.target) after login. Installing the
// router does kick off its initial navigation, but the Auth0 route guard awaits
// `isLoading` and the root redirect preserves the `?code`/`state` params (see
// router/index.ts), so the callback is never lost.
app.use(router);
app.use(store);
app.use(auth0 as any);
if (bugsnagVue) {
        app.use(bugsnagVue);
}

// initialize filestack (optional - only if API key is provided)
let client: any = null;
if (typeof FILESTACK_API_KEY !== 'undefined' && FILESTACK_API_KEY) {
        client = filestack.init(FILESTACK_API_KEY);
}
app.use(VueObserveVisibility);
app.use(VueSweetalert2);
app.use(toast);
// Register global components
app.component(
        'LoadingOverlay',
        Loading,
);
app.component(
        BaseLayoutModifier.name ?? 'BaseLayoutModifier',
        BaseLayoutModifier,
);
app.component(
        BaseBlock.name ?? 'BaseBlock',
        BaseBlock,
);
app.component(
        BaseBackground.name ?? 'BaseBackground',
        BaseBackground,
);
app.component(
        BasePageHeading.name ?? 'BasePageHeading',
        BasePageHeading,
);
app.component(
        BaseNavigation.name ?? 'BaseNavigation',
        BaseNavigation,
);
app.component(
        'CountryFlag',
        CountryFlag,
);
app.component(
        'Field',
        Field,
);

 
Object.keys(rules).forEach((rule) => {
        const validator = (rules as any)[rule];
        if (typeof validator === 'function') {
                defineRule(rule, validator);
        }
});

configure({
        generateMessage: localize({
                en,
                nl,
                fr,
                es,
        }),
});

// Register global directives
app.directive(
        'click-ripple',
        clickRipple,
);
app.directive(
        'toggle-class',
        toggleClass,
);

library.add(
        faLink,
        faUser,
        faPowerOff,
);
app.component(
        'FontAwesomeIcon',
        FontAwesomeIcon,
);

app.config.globalProperties.$client = client;

// Defer mounting until Auth0 has finished its redirect-callback / silent session
// check (isLoading === false). This prevents views from rendering and firing
// authenticated API calls before a token exists (which would be rejected and
// trigger yet another login redirect). `isLoading` is already `false` for the
// dev-bypass mock client, so the app mounts immediately in development.
// Capture whether this page load is an Auth0 redirect callback BEFORE the SDK
// processes (and clears) the `?code`/`state` params during its install. We only
// restore the user's original destination on a genuine post-login boot, so a
// stale remembered path can never hijack a normal page load.
const isAuthCallbackBoot = (() => {
        const search = window.location.search;
        return (search.includes('code=') || search.includes('error='))
                && search.includes('state=');
})();

// After the Auth0 callback, the SDK returns to the bare origin and the root
// redirect lands the user on /dashboard. If they originally requested a deep
// link, send them there instead. This runs as the final navigation (after the
// router's initial navigation has settled), so it deterministically wins over
// the default /dashboard destination.
const restoreReturnTo = () => {
        if (!isAuthCallbackBoot) {
                return;
        }
        const returnTo = consumeReturnTo();
        if (returnTo && returnTo !== '/' && returnTo !== '/dashboard') {
                router.isReady().then(() => {
                        router.replace(returnTo).catch(() => {
                                // Ignore navigation failures (e.g. redirected by
                                // the module-access guard) — the user stays on the
                                // best available page.
                        });
                });
        }
};

let appMounted = false;
let stopAuthLoadingWatch: (() => void) | null = null;
const mountWhenAuthReady = (isLoading: boolean | undefined) => {
        if (appMounted || isLoading !== false) {
                return;
        }
        appMounted = true;
        app.mount('#app');
        restoreReturnTo();
        if (stopAuthLoadingWatch) {
                stopAuthLoadingWatch();
        }
};
stopAuthLoadingWatch = watch(
        () => (auth0 as any).isLoading?.value,
        mountWhenAuthReady,
        { immediate: true },
);
// If the watcher fired synchronously (immediate) and already mounted, the stop
// handle wasn't assigned yet inside the callback — stop it now.
if (appMounted && stopAuthLoadingWatch) {
        stopAuthLoadingWatch();
}
